import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Form,
  Input,
  Select,
  Switch,
  Button,
  Space,
  Spin,
  Typography,
  Popconfirm,
  message,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined } from '@ant-design/icons';
import { usePermissions } from '@refinedev/core';
import { AxiosError } from 'axios';
import { authClient } from '../../providers/axios';
import { hasPermission } from '../../utils/permissions';

import { Permission } from './types';

const DELETE_PERMISSION = 'permissions:*';

export const PermissionDetailPage: React.FC = () => {
  const { key: permissionKey = '' } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [permission, setPermission] = useState<Permission | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Defense-in-depth UX gate: only operators holding permissions:* (e.g. super
  // admins) see the delete control. The auth-service enforces the same check
  // server-side (403) — hiding the button is not the real guard.
  const { data: userPermissions, isLoading: permsLoading } = usePermissions<string[]>();
  const canDelete = hasPermission(userPermissions ?? [], DELETE_PERMISSION);

  useEffect(() => {
    if (!permissionKey) return;

    const fetchPermission = async () => {
      setLoading(true);
      try {
        const { data: response } = await authClient.get(
          `/permissions/${encodeURIComponent(permissionKey)}`,
        );
        const payload = response.data ?? response;
        const perm = payload.permission ?? payload;
        setPermission(perm);
        form.setFieldsValue({
          description: perm.description,
          category: perm.category,
          scope: perm.scope ?? 'internal',
          isLive: perm.isLive,
        });
      } catch {
        message.error('Failed to load permission');
      } finally {
        setLoading(false);
      }
    };

    fetchPermission();
  }, [permissionKey, form]);

  const handleSave = async (values: {
    description: string;
    category: string;
    scope: string;
    isLive: boolean;
  }) => {
    setSaving(true);
    try {
      await authClient.patch(`/permissions/${encodeURIComponent(permissionKey)}`, values);
      message.success('Permission updated');
    } catch {
      message.error('Failed to update permission');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await authClient.delete(`/permissions/${encodeURIComponent(permissionKey)}`);
      message.success('Permission deleted');
      navigate('/permissions');
    } catch (err) {
      // Surface the backend's human-readable reason when present — notably the
      // 409 PERMISSION_IN_USE message ("Cannot delete permission — N role(s)
      // reference it"), and the 403 if the operator lacks permissions:*.
      const axiosErr = err as AxiosError<{ error?: { message?: string } }>;
      const backendMessage = axiosErr.response?.data?.error?.message;
      message.error(backendMessage || 'Failed to delete permission');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <Spin style={{ display: 'block', margin: '80px auto' }} />;
  }

  if (!permission) {
    return <Typography.Text type="danger">Permission not found</Typography.Text>;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/permissions')}>
          Back to Permissions
        </Button>
        {!permsLoading && canDelete && (
          <Popconfirm
            title="Delete this permission?"
            description={
              <span>
                Permanently delete <Typography.Text code>{permission.key}</Typography.Text>. This
                cannot be undone. Permissions still referenced by a role or user cannot be deleted.
              </span>
            }
            okText="Delete"
            okButtonProps={{ danger: true, loading: deleting }}
            cancelText="Cancel"
            onConfirm={handleDelete}
          >
            <Button danger icon={<DeleteOutlined />} loading={deleting}>
              Delete Permission
            </Button>
          </Popconfirm>
        )}
      </Space>

      <Card title="Permission Detail">
        <Descriptions bordered column={1} style={{ marginBottom: 24 }}>
          <Descriptions.Item label="Key">
            <Typography.Text code>{permission.key}</Typography.Text>
          </Descriptions.Item>
        </Descriptions>

        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: 'Description is required' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true, message: 'Category is required' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Scope"
            name="scope"
            rules={[{ required: true, message: 'Scope is required' }]}
          >
            <Select
              options={[
                { label: 'Internal', value: 'internal' },
                { label: 'Customer', value: 'customer' },
              ]}
            />
          </Form.Item>

          <Form.Item label="Live" name="isLive" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};
